import { UrgentDot } from "@/lib/urgent";
import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Home, Bell, Compass, Menu, Gamepad2, Mail, Gift } from "lucide-react";
import { PointsBadge } from "@/components/PointsBurst";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "./UserBits";
import { Logo } from "./Logo";
import { MourningRibbon, useMemorialActive } from "./MourningRibbon";
import { HeaderHeart } from "./csupport/HeaderHeart";

export const tabsFor = (uid) => [
  { to: "/feed", label: "Home", icon: Home, id: "feed" },
  { to: "/community", label: "Entdecken", icon: Compass, id: "community" },
  { to: "/spiele", label: "Spielen", icon: Gamepad2, id: "games", center: true },
  { to: "/geschenke", label: "Prämien", icon: Gift, id: "rewards" },
  { to: `/u/${uid}`, label: "Profil", icon: null, id: "profile" },
];

function useHideOnScroll() {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - last) < 8) return;
      setHidden(y > 80 && y > last);
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return hidden;
}

const Badge = ({ n, testId }) => n > 0 && <span key={n} data-testid={testId} className="badge-pop absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">{n > 9 ? "9+" : n}</span>;
const iconBtn = "relative w-9 h-9 rounded-full bg-rose-50 text-slate-700 flex items-center justify-center active:scale-90 transition-transform";

export function MobileHeader({ points, unread, unreadMsgs = 0 }) {
  const hidden = useHideOnScroll();
  const memorial = useMemorialActive();
  return (
    <div className="lg:hidden" data-testid="mobile-header">
      <div className={`flex items-center justify-between gap-2 px-3 overflow-hidden transition-[height,opacity] duration-200 ${hidden ? "h-0 opacity-0" : "h-14 opacity-100"}`}>
        <span className="min-w-0 shrink"><Logo to="/feed" narrow /></span>
        <div className="flex items-center gap-1.5 shrink-0">
          <HeaderHeart mobile />
          <Link to="/mitteilungen" aria-label="Mitteilungen" data-testid="mobile-nav-notifications" className={iconBtn}><Bell className="w-[18px] h-[18px]" /><Badge n={unread} testId="mobile-unread-badge" /></Link>
          <Link to="/nachrichten" aria-label="Nachrichten" data-testid="mobile-messages-btn" className={iconBtn}><Mail className="w-[18px] h-[18px]" /><Badge n={unreadMsgs} testId="mobile-messages-badge" /></Link>
          <PointsBadge points={points} testId="mobile-points-badge" className="h-9 px-2.5" />
          <Link to="/menu" aria-label="Menü" data-testid="mobile-nav-menu" className={iconBtn}><Menu className="w-[18px] h-[18px]" /></Link>
        </div>
      </div>
      {memorial && <div className={`flex justify-center overflow-hidden transition-[height,opacity] duration-200 ${hidden ? "h-0 opacity-0" : "h-5 opacity-100"}`} data-testid="mobile-mourning-strip">
        <MourningRibbon testId="mobile-mourning-ribbon" inline />
      </div>}
    </div>
  );
}

const TabIcon = ({ t, user, active }) => t.id === "profile"
  ? <span className={`rounded-full transition-shadow ${active ? "ring-2 ring-rose-500 ring-offset-2 ring-offset-white" : ""}`}><UserAvatar userId={user?.user_id} name={user?.name} picture={user?.picture} size="w-6 h-6" text="text-[10px]" crown={false} noStory /></span>
  : <t.icon className={`w-[22px] h-[22px] ${active ? "stroke-[2.4]" : ""}`} />;

function CenterTab({ t, gamesWaiting }) {
  return (
    <NavLink to={t.to} aria-label={t.label} data-testid={`mobile-nav-${t.id}`} className="relative flex flex-col items-center justify-end h-16 pb-1.5">
      {({ isActive }) => (<>
        <span className={`tabbar-center absolute -top-5 w-14 h-14 rounded-full pink-gradient text-white flex items-center justify-center ring-4 ring-white ${isActive ? "is-active" : ""}`}>
          <t.icon className="w-6 h-6 stroke-[2.3]" />
          {gamesWaiting && <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-amber-300 ring-2 ring-rose-500 tile-dot" data-testid="mobile-games-dot" />}
        </span>
        <span className={`text-[10px] leading-none ${isActive ? "font-extrabold text-rose-600" : "font-semibold text-slate-500"}`}>{t.label}</span>
      </>)}
    </NavLink>
  );
}

export function BottomTabs({ urgent = false, gamesWaiting = false }) {
  const { user } = useAuth();
  useEffect(() => {
    document.body.classList.add("has-tabbar");
    return () => document.body.classList.remove("has-tabbar");
  }, []);
  return (
    <nav className="lg:hidden fixed z-40 inset-x-3 bottom-[max(0.6rem,env(safe-area-inset-bottom))] tabbar grid grid-cols-5 items-end rounded-[26px] px-1" data-testid="mobile-tabs">
      {tabsFor(user?.user_id).map((t) => t.center ? <CenterTab key={t.id} t={t} gamesWaiting={gamesWaiting} /> : (
        <NavLink key={t.id} to={t.to} aria-label={t.label} data-testid={`mobile-nav-${t.id}`} className="relative flex flex-col items-center justify-center gap-1 h-16 active:scale-95 transition-transform">
          {({ isActive }) => (<>
            <span className={`relative flex items-center justify-center w-12 h-8 rounded-2xl transition-[background-color,color,transform] duration-300 ${isActive ? "bg-rose-100 text-rose-600 -translate-y-0.5" : "text-slate-500"}`}>
              <TabIcon t={t} user={user} active={isActive} />
              {t.id === "feed" && <UrgentDot on={urgent} testId="mobile-feed-urgent" />}
            </span>
            <span className={`text-[10px] leading-none ${isActive ? "font-extrabold text-rose-600" : "font-semibold text-slate-500"}`}>{t.label}</span>
            {isActive && <span className="absolute bottom-1 w-1 h-1 rounded-full bg-rose-500 tab-glow" />}
          </>)}
        </NavLink>
      ))}
    </nav>
  );
}
